import { useCallback, useEffect, useState } from 'react';

import { useCookieConsent } from './useCookieConsent';

import { CookieCategory } from '@shared-types/optanon.types';
import { Warn } from '@utilities/logging';

export const useLocalStorage = <T>(key: string, initialValue: T, cookieCategory = CookieCategory.ESSENTIAL) => {
  const accepted = useCookieConsent(cookieCategory);

  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      Warn(error);
      return initialValue;
    }
  });

  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      setStoredValue((state) => (value instanceof Function ? value(state) : value));
    } catch (error) {
      Warn(error);
    }
  }, []);

  useEffect(() => {
    if (!accepted) return;

    try {
      localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      Warn(error);
    }
  }, [accepted, key, storedValue]);

  return [storedValue, setValue] as const;
};
